import {
  AchAccountType,
  useAchInstitutionName,
  useSubmitEnrollmentHYCSFundAccountData,
  type EnrollmentHYCSFundAccountSubmitInput,
  type EnrollmentHYCSFundAccountSubmitOutput,
} from '@egw/api'
import {
  Button,
  ButtonVariant,
  IconConfirmationCircle,
  IconErrorCircle,
  Input,
  Link,
  RadioGroup,
} from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import cx from 'classnames'
import { useState, type Dispatch, type FC, type SetStateAction } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { EDataLayerEventWealth } from '../../constants/events/EDataLayerEventWealth'
import { LinkingType } from '../../types/account-type'
import { BankName } from '../bank-name/bank-name'

const log = new Logger('External Account Manual Linking')

interface ManualLinkProps {
  setLinkingType: Dispatch<SetStateAction<LinkingType>>
  onAccountLinkSuccess: Dispatch<SetStateAction<string | null>>
  onClose: () => void
  applicationId: string | null | undefined
}

interface FormData {
  accountType: string
  routingNumber: string
  accountNumber: string
  verifyAccountNumber: string
  nickname: string
}

export const ManualLink: FC<ManualLinkProps> = ({ setLinkingType, onClose, onAccountLinkSuccess, applicationId }) => {
  const [step, setStep] = useState(0)
  const { t } = useTranslation('features-enrollment')
  const [linkSuccess, setLinkSuccess] = useState<boolean>(false)
  const [linkFailure, setLinkFailure] = useState<boolean>(false)
  const [accountName, setAccountName] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid },
    watch,
    setValue,
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      accountType: '',
      routingNumber: '',
      accountNumber: '',
      verifyAccountNumber: '',
      nickname: '',
    },
  })

  const { data: achBankNameResponse, isFetching: isAchBankFetching } = useAchInstitutionName(watch('routingNumber'), {
    enabled: Boolean(watch('routingNumber').length === 9),
  })

  const {
    mutate: linkAchAccount,
    isLoading,
    error,
  } = useSubmitEnrollmentHYCSFundAccountData({
    onSuccess: (res: EnrollmentHYCSFundAccountSubmitOutput, vars?: EnrollmentHYCSFundAccountSubmitInput) => {
      // If RES is a success and the account is active, set the link success state
      if (res.__typename === 'SubmitEnrollmentSuccess') {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_LINKACCOUNT_VERIFIED, { applicationId })
        const fundingAccountName = vars?.fundingAccountName ?? null
        onAccountLinkSuccess(fundingAccountName)
        setAccountName(fundingAccountName)
        setLinkSuccess(true)
        setStep(2)
        return
      }
      // If RES is an Error, set the link failure state
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_LINKACCOUNT_FAILED, { applicationId })
      setLinkFailure(true)
    },
    onError: () => {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_LINKACCOUNT_FAILED, { applicationId })
      setLinkFailure(true)
    },
  })

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    if (isLoading) return
    const formData = fData as FormData
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_LINKMANUALLY_SUBMIT, { applicationId })
    try {
      await linkAchAccount({
        fundingAccountName: formData.nickname,
        fundingAccountNumber: formData.accountNumber,
        fundingRoutingNumber: formData.routingNumber,
        fundingAchType: formData.accountType as AchAccountType,
        linkAccount: true,
      })
    } catch (e) {
      setLinkFailure(true)
      log.error('Error linking ACH account: ', e)
    }
  })

  return (
    <>
      {step === 0 ? (
        <>
          <p className="font-semibold mb-3xs">{t('accounts.manual.title')}</p>
          <p className="mb-xs text-body">{t('accounts.manual.message')}</p>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => {
              sendEvent(EDataLayerEventWealth.BANKHYCHYS_LINKMANUALLY_INTENT, { applicationId })
              setStep(1)
              setLinkingType(LinkingType.MANUAL)
            }}
            testId="add_account_link_manually_secondary_btn"
            id="add_account_link_manually_secondary_btn"
          >
            {t('accounts.manual.cta')}
          </Button>
        </>
      ) : null}

      {step === 1 ? (
        <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-2xs mt-2xs">
          <p className="mb-md typo-primary-base-regular">{t('hycs.fund_account.manual_link_description')}</p>

          <Controller
            name="accountType"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <p className="mb-md typo-primary-base-semibold">{t('manual_link.account_type')}</p>
                <RadioGroup
                  {...field}
                  options={[
                    { label: t('manual_link.checking'), value: AchAccountType.CHECKING },
                    { label: t('manual_link.savings'), value: AchAccountType.SAVING },
                  ]}
                  testId="account-type_input"
                  id="account-type"
                  onChange={(v) => {
                    field.onChange(v)
                    setValue('accountType', v as string, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }}
                  isPII
                />
              </>
            )}
          />

          <div>
            <Controller
              name="routingNumber"
              control={control}
              rules={{
                required: t('manual_link.required_field'),
                // 9 digits validation
                pattern: {
                  value: /^\d{9}$/,
                  message: t('manual_link.invalid_routing_number'),
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('manual_link.routing_number')}
                  testId="routing-number_input"
                  id="routing-number"
                  required
                  valueFormatter={(v) => v.replace(/[^0-9]/g, '')}
                  error={(() => {
                    if (achBankNameResponse?.__typename === 'GetBankInstitutionNameError') {
                      return t('manual_link.bank_verification_failure')
                    }
                    if (formErrors.routingNumber && achBankNameResponse?.__typename !== 'BankingInstitution') {
                      return formErrors.routingNumber.message
                    }
                  })()}
                  isPII
                />
              )}
            />
            {(achBankNameResponse ?? isAchBankFetching) ? (
              <BankName
                bankName={
                  achBankNameResponse?.__typename === 'BankingInstitution' ? achBankNameResponse.bankName : undefined
                }
                isFetching={isAchBankFetching}
              />
            ) : null}
          </div>

          <Controller
            name="accountNumber"
            control={control}
            rules={{
              required: t('manual_link.required_field'),
              validate: (value) => (value.length >= 5 && value.length < 18) || t('manual_link.invalid_account_number'), // Length validation
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('manual_link.account_number')}
                testId="account-number_input"
                id="account-number"
                valueFormatter={(v) => v.replace(/[^0-9]/g, '')}
                required
                error={formErrors.accountNumber?.message}
                maxLength={17}
                isPII
              />
            )}
          />

          <Controller
            name="verifyAccountNumber"
            control={control}
            rules={{
              required: t('manual_link.required_field'),
              validate: (value) => {
                if (value !== watch('accountNumber')) {
                  return t('manual_link.account_numbers_must_match')
                }
                if (value.length < 6 || value.length > 17) {
                  return t('manual_link.invalid_account_number') // Length validation
                }
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('manual_link.verify_account')}
                testId="account-number-verify_input"
                id="account-number-verify"
                valueFormatter={(v) => v.replace(/[^0-9]/g, '')}
                required
                error={formErrors.verifyAccountNumber?.message}
                maxLength={17}
                isPII
              />
            )}
          />

          <Controller
            name="nickname"
            control={control}
            rules={{
              required: t('manual_link.required_field'),
              // matches Plaid Account Name length
              maxLength: 30,
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('manual_link.nickname')}
                testId="account-name_input"
                id="account-name"
                required
                error={formErrors.nickname?.message}
                // matches Plaid Account Name length
                maxLength={30}
              />
            )}
          />

          <Button
            type="submit"
            isDisabled={!isValid}
            isLoading={isLoading}
            testId="btnLinkManual_button"
            id="submitAddAccount_primary_btn"
            className={cx({
              'cursor-not-allowed touch-none': isLoading,
            })}
          >
            {isLoading ? t('manual_link.linking_account') : t('manual_link.link_account')}
          </Button>
        </form>
      ) : null}

      {/* Loading, error, success states for saving a new account */}
      {step === 2 && (
        <>
          {linkSuccess ? (
            <>
              <IconConfirmationCircle className="mt-xs mb-sm" />
              <p className="font-light text-header text-xl mb-2xs">{t('manual_link.account_verified_title')}</p>
              <p className="mb-xs">
                {t('manual_link.account_verified_message', {
                  accountName,
                })}
              </p>
              <Button
                onClick={() => {
                  setLinkFailure(false)
                  setLinkSuccess(false)
                  onClose()
                }}
                className="mb-2xs"
                testId="account-add-success_button"
              >
                {t('manual_link.done')}
              </Button>
            </>
          ) : null}

          {linkFailure || error ? (
            <>
              <IconErrorCircle className="mt-xs mb-lg" />
              <p className="font-light text-header text-xl mb-2xs">{t('manual_link.error_title')}</p>
              <p className="mb-xs">{t('manual_link.error_p1')}</p>
              <p className="mb-xs">{t('manual_link.error_p2')}</p>
              <button
                type="button"
                className="text-left flex items-center cursor-pointer py-xs group border-b border-light"
                onClick={() => {
                  setLinkFailure(false)
                  setStep(1)
                }}
                data-testid="account-add-error_button"
              >
                <p className="flex-1 text-body">{t('manual_link.try_different_account')}</p>
                <ChevronRightIcon className="h-5 w-5 fill-body opacity-50 group-hover:opacity-100" />
              </button>
              <Link href={WEALTH_ROUTES.HELP_CENTER.path} className="flex items-center py-xs group font-normal">
                <p className="flex-1 text-body">{t('manual_link.get_help')}</p>
                <ChevronRightIcon className="h-5 w-5 fill-body opacity-50 group-hover:opacity-100" />
              </Link>
            </>
          ) : null}
        </>
      )}
    </>
  )
}
